/* eslint-disable max-lines */
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components';
import { ComboboxContextProvider } from '@/components/Combobox/context/ComboboxContextProvider';
import { useComboboxContext } from '@/components/Combobox/context/useComboboxContext';
import {
  ComboboxContextProviderProps,
  ComboboxOptionBase,
} from '@/components/Combobox/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { InputGroup, InputGroupAddon } from '@/components/ui/input-group';
import { cn } from '@/lib/utils';
import {
  faAnglesUpDown,
  faFilter,
  faSquare,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faCheckSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Children, ComponentProps, isValidElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type ComboboxProps<T extends ComboboxOptionBase> =
  ComboboxContextProviderProps<T>;

function Combobox<T extends ComboboxOptionBase>(props: ComboboxProps<T>) {
  return (
    <ComboboxContextProvider<T> {...props}>
      <Drawer>
        <Popover>{props.children}</Popover>
      </Drawer>
    </ComboboxContextProvider>
  );
}

function ComboboxTrigger() {
  const { showClear, disabled } = useComboboxContext();

  const renderTriggerContent = (mobile: boolean) => (
    <InputGroup
      aria-disabled={disabled}
      className={cn(
        'p-3 md:flex justify-between items-start border rounded-md h-auto min-h-10 hidden gap-2',
        mobile && 'flex md:hidden',
        disabled && 'bg-neutrals-200 text-neutrals-700',
      )}>
      <Chips />
      <InputGroupAddon align={'inline-end'} className="gap-1 p-0">
        {showClear && <ClearButton />}
        <FontAwesomeIcon icon={faAnglesUpDown} />
      </InputGroupAddon>
    </InputGroup>
  );

  return (
    <>
      <PopoverTrigger
        className="hidden md:flex w-full"
        disabled={disabled}
        asChild>
        {renderTriggerContent(false)}
      </PopoverTrigger>
      <DrawerTrigger
        className="flex md:hidden w-full"
        disabled={disabled}
        asChild>
        {renderTriggerContent(true)}
      </DrawerTrigger>
    </>
  );
}

type ComboboxContentChildren<T extends ComboboxOptionBase> = (
  options: T[],
) => ReactNode;

type ComboboxContentProps<T extends ComboboxOptionBase> = Omit<
  ComponentProps<typeof PopoverContent>,
  'children'
> & {
  children?: ReactNode | ComboboxContentChildren<T>;
  filterComponent?: ReactNode;
};

function ComboboxContent<T extends ComboboxOptionBase>({
  children,
  filterComponent,
  className,
  ...props
}: ComboboxContentProps<T>) {
  const { t } = useTranslation();
  const { options } = useComboboxContext<T>();
  const childrenNodes = Children.toArray(
    typeof children === 'function' ? children(options) : (children ?? []),
  );
  const content = childrenNodes.length > 0 ? childrenNodes : <DefaultItems />;

  const renderCommand = (
    <Command
      filter={(val, search) => {
        return options
          .find((o) => o.value === val)
          ?.label.toLowerCase()
          .includes(search.toLowerCase())
          ? 1
          : 0;
      }}>
      <div className="flex items-center gap-1 m-3">
        <CommandInput
          placeholder={t('combobox.searchPlaceholder')}
          className="w-full"
          wrapperClassName="m-0 w-full"
        />
        {filterComponent && isValidElement(filterComponent)
          ? filterComponent
          : null}
      </div>
      <CommandList>
        <CommandEmpty>{t('combobox.noOptions')}</CommandEmpty>
        {content}
      </CommandList>
    </Command>
  );

  return (
    <>
      <PopoverContent className={cn('min-w-72 p-0', className)} {...props}>
        {renderCommand}
      </PopoverContent>
      <DrawerContent className={cn('min-w-72 p-0', className)} {...props}>
        {renderCommand}
      </DrawerContent>
    </>
  );
}

type ComboboxFilterChildren = (
  filter: string[],
  toggleFilter: (filter: string) => void,
) => ReactNode;

function ComboboxFilter<T extends ComboboxOptionBase>({
  children,
}: {
  children: ComboboxFilterChildren;
}) {
  const { filterValues, toggleFilter } = useComboboxContext<T>();
  const childrenNodes = Children.toArray(children(filterValues, toggleFilter));
  const { t } = useTranslation();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className=" bg-neutrals-200 hover:bg-neutrals-400 text-neutrals-900">
          <FontAwesomeIcon
            icon={faFilter}
            className="size-5 text-neutrals-900"
          />
          {filterValues.length > 0 && (
            <Badge
              type={'pill'}
              className="ml-1 bg-neutrals-400 hover:bg-neutrals-400 text-neutrals-900">
              {filterValues.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-44 p-0" align="end">
        {childrenNodes.length > 0 ? childrenNodes : t('combobox.noFilter')}
      </PopoverContent>
    </Popover>
  );
}

function DefaultItems() {
  const { options } = useComboboxContext();

  return (
    <>
      {options.map((option) => (
        <ComboboxItem key={option.value} value={option.value}>
          <div className="flex gap-2 items-center">
            {option.color && (
              <div
                className={`w-2.5 h-2.5 rounded-full`}
                style={{ backgroundColor: option.color }}
              />
            )}
            <span className="text-neutrals-900 text-sm font-normal leading-5">
              {option.label}
            </span>
          </div>
        </ComboboxItem>
      ))}
    </>
  );
}

type ComboboxItemProps = Pick<ComboboxOptionBase, 'value'> & {
  children: ReactNode;
};

function ComboboxItem({ value, children }: ComboboxItemProps) {
  const { toggleByValue, isSelected } = useComboboxContext();

  return (
    <CommandItem value={value} onSelect={() => toggleByValue(value)}>
      <div className="flex items-center justify-between w-full gap-2 rounded-md">
        {children}
        {isSelected(value) ? (
          <FontAwesomeIcon
            icon={faCheckSquare}
            className="text-primary-blue-600 size-5"
          />
        ) : (
          <FontAwesomeIcon
            icon={faSquare}
            className="text-neutrals-300 size-5"
          />
        )}
      </div>
    </CommandItem>
  );
}

function Chips() {
  const { t } = useTranslation();
  const { selectedOptions, placeholder, maxShownChips, disabled } =
    useComboboxContext();

  const optionsToShow = maxShownChips
    ? selectedOptions.slice(0, maxShownChips)
    : selectedOptions;

  const remainingCount = maxShownChips
    ? selectedOptions.length - maxShownChips
    : 0;
  return (
    <div className={cn('flex flex-wrap gap-1')}>
      {optionsToShow.length > 0 ? (
        optionsToShow.map((option) => (
          <Chip
            key={option.value}
            option={option}
            className={disabled && 'bg-neutrals-300'}
          />
        ))
      ) : (
        <span className="text-neutrals-700 text-sm font-normal leading-5">
          {placeholder ?? t('combobox.placeholder')}
        </span>
      )}
      {remainingCount > 0 && (
        <Badge variant={'secondary'} className="bg-neutrals-200">
          +{remainingCount}
        </Badge>
      )}
    </div>
  );
}

function Chip<T extends ComboboxOptionBase>({
  option,
  className,
}: {
  option: T;
  className?: string;
}) {
  const { removeValueFromSelectedOptions, customChipContent, disabled } =
    useComboboxContext<T>();
  return (
    <Badge
      variant={'secondary'}
      className={cn('flex gap-1 bg-neutrals-200  max-w-32', className)}>
      {customChipContent ? (
        customChipContent(option)
      ) : (
        <>
          {option.color && (
            <div
              className={`size-2 rounded-full shrink-0`}
              style={{ backgroundColor: option.color }}
            />
          )}
          <span className="truncate">{option.label}</span>
        </>
      )}
      <Button
        variant="ghost"
        size="xs"
        className="size-3"
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          removeValueFromSelectedOptions(option.value);
        }}>
        <FontAwesomeIcon icon={faXmark} className="size-3 cursor-pointer" />
      </Button>
    </Badge>
  );
}

function ClearButton() {
  const { clear, disabled } = useComboboxContext();
  return (
    <Button
      disabled={disabled}
      variant={'circle'}
      className="size-5 cursor-pointer text-neutrals-700"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        clear();
      }}>
      <FontAwesomeIcon icon={faXmark} />
    </Button>
  );
}

export {
  Combobox,
  ComboboxContent,
  ComboboxFilter,
  ComboboxItem,
  ComboboxTrigger,
};
